<template>
  <div class="pro-player-info">
    <div class="item-image">
      <Thumb
        :font-size="20"
        :height="20"
        :image-height="200"
        :image-width="200"
        :item="{} as Item"
        :size="20"
        :src="getImageFromProxy(info.general?.avatar || '', '300:300')"
        :style="{
          '--bg-url': `url(${info.general?.team_logo})`,
        }"
        image-sizes="500:500"
        loading="eager"
      />
    </div>
    <div class="item-info">
      <div class="item-info__header">
        <div class="f fs gap8">
          <h1 class="item-name">
            {{ info.general?.nick_name }}
          </h1>
          <div
            v-tooltip="info.general?.team || ''"
            class="f"
          >
            <img
              :src="getImageFromProxy(info.general?.team_logo || '', '50:50')"
              alt=""
              height="32"
              width="32"
            >
          </div>
        </div>
        <div class="socials f fs gap12">
          <a
            v-if="info.social?.twitter"
            :href="info.social?.twitter"
            aria-label="Twitter"
            class="btn btn-primary"
            rel="noopener nofollow"
            target="_blank"
          >
            <svg
              height="22"
              width="22"
            >
              <use xlink:href="/icons/sprite.svg#i-twitter" />
            </svg>
          </a>
          <a
            v-if="info.social?.instagram"
            :href="info.social?.instagram"
            aria-label="Instagram"
            class="btn btn-primary"
            rel="noopener nofollow"
            target="_blank"
          >
            <svg
              height="22"
              width="22"
            >
              <use xlink:href="/icons/sprite.svg#i-instagram-2" />
            </svg>
          </a>
          <a
            v-if="info.social?.twitch"
            :href="info.social?.twitch"
            aria-label="Twitch"
            class="btn btn-primary"
            rel="noopener nofollow"
            target="_blank"
          >
            <svg
              height="22"
              width="22"
            >
              <use xlink:href="/icons/sprite.svg#i-twitch" />
            </svg>
          </a>
          <a
            v-if="info.social?.youtube"
            :href="info.social?.youtube"
            aria-label="Youtube"
            class="btn btn-primary"
            rel="noopener nofollow"
            target="_blank"
          >
            <svg
              height="22"
              width="22"
            >
              <use xlink:href="/icons/sprite.svg#i-youtube" />
            </svg>
          </a>
          <a
            v-if="info.social?.steam"
            :href="info.social?.steam"
            aria-label="Steam"
            class="btn btn-primary"
            rel="noopener nofollow"
            target="_blank"
          >
            <svg
              height="22"
              width="22"
            >
              <use xlink:href="/icons/sprite.svg#i-steam" />
            </svg>
          </a>
        </div>
      </div>
      <p class="item-info__description">
        {{ info.general?.description }}
      </p>
      <div class="item-info__footer">
        <div class="footer-item">
          <p class="light fw600 mb8">
            {{ t('name') }}
          </p>
          <p class="fw600">
            {{ info.general?.real_name || 'N/A' }}
          </p>
        </div>
        <div class="footer-item">
          <p class="light fw600 mb8">
            {{ t('Team') }}
          </p>
          <p class="fw600">
            {{ info.general?.team || 'N/A' }}
          </p>
        </div>
        <div
          v-if="info.general?.birthday"
          class="footer-item"
        >
          <p class="light fw600 mb8">
            {{ t('birthday') }}
          </p>
          <p class="fw600">
            {{ new Date(info.general?.birthday).toLocaleDateString() }}
          </p>
        </div>
        <div class="footer-item">
          <p class="light fw600 mb8">
            {{ t('country') }}
          </p>
          <p class="fw600">
            {{ info.general?.country || 'N/A' }}
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import type { Item, ProPlayerWithItems } from '~/graphql/generated/schema-types';
import Thumb from '~/components/Pages/Item/Components/Children/Thumb.vue';

defineProps<{
  info: ProPlayerWithItems
}>();
const { t } = useI18n();
</script>

<style lang="scss" scoped>
.pro-player-info {
  width: 100%;
  display: flex;
  align-items: flex-start;
  gap: 4.8rem;
  color: var(--surfaces-on-surface);

  .item-image {
    width: 200px;
    height: 200px;
    flex-shrink: 0;
    border-radius: 50%;
    overflow: hidden;

    &:deep(.thumb_bg) {
      color: var(--accent-secondary);
      inset: 0;

      &:after, &:before {
        content: '';
        background-image: var(--bg-url);
        filter: blur(50px);
        background-size: 150%;
        background-position: center;
      }
    }

    &:deep(.thumb) {
      padding: 0;
      border-radius: 50%;
      overflow: hidden;
    }

    &:deep(.thumb img) {
      border-radius: 50%;
    }

    //@media screen and (max-width: 525px) {
    //  width: 100%;
    //  min-width: 100%;
    //}
  }

  .item-info {
    display: flex;
    flex-direction: column;
    gap: 20px;
    width: 100%;

    .socials {
      .btn {
        padding-left: 9px;
        padding-right: 9px;
        color: var(--surfaces-on-surface-variable);

        &:hover {
          color: var(--accent-on-accent-secondary);
        }
      }
    }

    &__header {
      display: flex;
      align-items: flex-start;
      justify-content: space-between;
      gap: 24px;

      @media screen and (max-width: 768px) {
        flex-direction: column;
        align-items: center;
      }
    }

    &__footer {
      display: flex;
      align-items: center;
      flex-wrap: wrap;
      gap: 24px 80px;
      font-size: 16px;
      overflow: hidden;

      .footer-item:not(:last-child) {
        position: relative;

        &:after {
          content: '';
          position: absolute;
          right: -40px;
          top: 50%;
          transform: translateY(-50%);
          display: block;
          width: 2px;
          height: 70%;
          background: var(--surfaces-outline);
        }
      }
    }

    .item-name {
      font-weight: 600;
      font-size: 2.8rem;
      line-height: 3.8rem;

    }

    &__description {
      //font-size: 1.6rem;
      //color: var(--surfaces-on-surface-variable);
      //margin-bottom: 20px;
      max-width: 61rem;
      color: var(--surfaces-on-surface-variable);
      font-kerning: none;
      font-size: 16px;
      font-style: normal;
      font-weight: 400;
      line-height: 32px;
    }
  }

  @media screen and (max-width: 1075px) {
    .item-name {
      font-size: 2.4rem;
      line-height: 3.4rem;
    }

    .item-description {
      font-size: 1.4rem;
      line-height: 3rem;
    }
  }

  @media screen and (max-width: 850px) {
    flex-direction: column;
    justify-content: center;
    align-items: center;

    .item-info {
      align-items: center;
      width: 100%;
    }
  }
  @media screen and (max-width: 768px) {
    gap: 0;

    .item-info {
      margin-left: 0;
      margin-top: 1.6rem;

      .item-name {
        font-size: 2.2rem;
        line-height: 3.2rem;
      }

      .item-description {
        margin-top: 1.2rem;
        line-height: 2.8rem;
      }
    }
  }

  @media screen and (max-width: 525px) {
    .item-image {
      margin-left: 1.5rem;
      margin-right: 1.5rem;
      background: rgba(33, 40, 53, 0.2);
      border-radius: 1.2rem;
    }

    .item-info {
      .item-name {
        font-size: 2rem;
        line-height: 2.7rem;
      }

      .item-description {
        margin-top: 0.8rem;
        line-height: 2.6rem;
      }
    }
  }
}
</style>
